// Portal users (admin only) — the React port of /users.php. Manages the
// `portal_users` sign-in accounts: list / create / update / delete, with
// the same guard rails as the legacy page (immutable usernames on edit,
// ≥6-char passwords, no deleting yourself or the last enabled admin).

import type { ApiClient } from "./client.js";
import type { PortalUserDeleteResult, PortalUserSaveRequest, PortalUserSaveResult, PortalUsersListResponse } from "./types.js";

export function makeUsersApi(client: ApiClient) {
  return {
    /** Every portal account, ordered by username. */
    list() {
      return client.get<PortalUsersListResponse>("/v1/api/users");
    },
    /** Create (`id: 0`) or update a user. Empty/omitted `password` keeps the
     *  stored hash on update; it's required on create. Username changes are
     *  ignored on update. 409 on a duplicate username. */
    save(req: PortalUserSaveRequest) {
      const isReseller = req.role === "reseller";
      const b: Record<string, unknown> = {
        id: req.id,
        username: req.username,
        full_name: req.full_name,
        role: req.role,
        reseller_id: isReseller ? (req.reseller_id ?? null) : null,
        enabled: req.enabled,
        // Device grants only mean anything for a reseller account; send []
        // for other roles so the backend clears any stale grants.
        grant_olts: isReseller ? (req.grant_olts ?? []) : [],
        grant_routers: isReseller ? (req.grant_routers ?? []) : [],
      };
      if (req.password) b.password = req.password;
      return client.post<PortalUserSaveResult>("/v1/api/users/save", b);
    },
    /** Delete a user. Refused for your own account or the only enabled admin. */
    remove(id: number) {
      return client.post<PortalUserDeleteResult>("/v1/api/users/delete", { id });
    },
  };
}

export type UsersApi = ReturnType<typeof makeUsersApi>;
